import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Plus, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STAGES = ["New", "Interested", "Demo", "Admitted", "Lost"];
const STAGE_COLOR = {
  New: "#0A66C2", Interested: "#915907", Demo: "#5A2A82",
  Admitted: "#057642", Lost: "#CC1016",
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", phone:"", email:"", source:"Website", status:"New", course:"" });

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/leads", form);
    setOpen(false);
    setForm({ name:"", phone:"", email:"", source:"Website", status:"New", course:"" });
    toast.success("Lead added");
    load();
  };

  const moveTo = async (lead, status) => {
    await api.put(`/leads/${lead.id}`, { ...lead, status });
    toast.success(`Moved to ${status}`);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Leads / CRM"
        subtitle="Kanban pipeline across inquiry channels"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="add-lead-btn">
                <Plus size={16}/> New lead
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add lead</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <input required placeholder="Full name" data-testid="lead-name-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
                <input required placeholder="Phone" data-testid="lead-phone-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
                <input placeholder="Email" data-testid="lead-email-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
                <input placeholder="Interested course" data-testid="lead-course-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.course} onChange={e=>setForm({...form, course:e.target.value})}/>
                <Select value={form.source} onValueChange={v=>setForm({...form, source:v})}>
                  <SelectTrigger data-testid="lead-source-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["Website","Walk-in","Phone","WhatsApp","Facebook","Google Ads"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <button type="submit" className="btn-primary" data-testid="lead-save-btn">Save lead</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="kanban-board">
        {STAGES.map((s) => {
          const col = leads.filter((l) => l.status === s);
          return (
            <div key={s} className="kanban-col" data-testid={`kanban-col-${s.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{background: STAGE_COLOR[s]}}></span>
                  <span className="text-sm font-semibold">{s}</span>
                </div>
                <span className="text-xs text-[#00000099]">{col.length}</span>
              </div>
              <div className="space-y-2">
                {col.map((lead) => (
                  <div key={lead.id} className="bg-white border border-[#E0DFDC] rounded-md p-3 hover:shadow-sm" data-testid={`lead-card-${lead.id}`}>
                    <div className="font-semibold text-[14px] text-[#000000E6]">{lead.name}</div>
                    <div className="text-[12px] text-[#00000099] mt-0.5">{lead.course}</div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-[#00000099]">
                      <span className="inline-flex items-center gap-1"><Phone size={12}/>{lead.phone}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#00000099] mt-0.5">
                      <span className="inline-flex items-center gap-1"><Mail size={12}/>{lead.email}</span>
                    </div>
                    <div className="mt-2 pt-2 border-t border-[#E0DFDC]/70 flex items-center justify-between">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4F2EE] text-[#00000099] uppercase tracking-wider font-semibold">{lead.source}</span>
                      <Select value={lead.status} onValueChange={(v)=>moveTo(lead, v)}>
                        <SelectTrigger className="h-7 text-xs w-[105px]"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {STAGES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {col.length === 0 && <div className="text-xs text-[#00000066] px-2 py-6 text-center">No leads</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
